const RAW_API_BASE = import.meta.env.VITE_API_BASE || "/api";
const API_BASE = RAW_API_BASE.replace(/\/+$/, "");

interface RequestOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

class ApiError extends Error {
  status: number;
  data: unknown;
  constructor(status: number, message: string, data?: unknown) {
    super(message);
    this.status = status;
    this.data = data;
  }
}

function getToken(): string | null {
  return localStorage.getItem("token") || sessionStorage.getItem("token");
}

function setToken(token: string, remember = true) {
  if (remember) {
    localStorage.setItem("token", token);
    sessionStorage.removeItem("token");
  } else {
    sessionStorage.setItem("token", token);
    localStorage.removeItem("token");
  }
}

function clearToken() {
  localStorage.removeItem("token");
  sessionStorage.removeItem("token");
  // 清除用户级 session 缓存，避免切换账号后仍看到上个账号的 UI 状态
  // 包括：ai-chat (gsd_*)、智能文档 (doc-gen:*)、其他带命名空间的 key
  try {
    const PREFIX_WHITELIST = ["gsd_", "doc-gen:", "smart-doc:", "workbench:"];
    Object.keys(sessionStorage)
      .filter((k) => PREFIX_WHITELIST.some((p) => k.startsWith(p)))
      .forEach((k) => sessionStorage.removeItem(k));
  } catch {
    // ignore
  }
}

function isAuthenticated(): boolean {
  return !!getToken();
}

function getApiBase(): string {
  return API_BASE;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, headers = {}, signal } = options;
  const token = getToken();

  const fetchHeaders: Record<string, string> = {
    ...headers,
  };

  if (token) {
    fetchHeaders["Authorization"] = `Bearer ${token}`;
  }

  if (body && !(body instanceof FormData)) {
    fetchHeaders["Content-Type"] = "application/json";
  }

  const fetchOptions: {
    method: string;
    headers: Record<string, string>;
    signal?: AbortSignal;
    body?: FormData | string | null;
  } = {
    method,
    headers: fetchHeaders,
    signal,
  };

  if (body) {
    fetchOptions.body = body instanceof FormData ? body : JSON.stringify(body);
  }

  const response = await fetch(`${API_BASE}${path}`, fetchOptions);

  // 登录/公钥/注册等公开接口自己处理错误消息，不触发全站 401 跳转，否则登录失败会被立即刷页清掉错误提示
  const isAuthOpenEndpoint = /^\/auth\/(login|login-json|public-key|register|forgot-password)/.test(path);

  if (response.status === 401 && !isAuthOpenEndpoint) {
    clearToken();
    window.location.href = "/login";
    throw new ApiError(401, "未授权，请重新登录");
  }

  if (!response.ok) {
    let errorData: unknown;
    try {
      errorData = await response.json();
    } catch {
      errorData = null;
    }
    const detailMessage = (errorData as { detail?: string })?.detail;
    const message =
      response.status === 401
        ? detailMessage || "用户名或密码错误"
        : response.status === 403
        ? "你没有相关权限，请联系管理员"
        : detailMessage || `请求失败 (${response.status})`;
    throw new ApiError(response.status, message, errorData);
  }

  return response.json() as Promise<T>;
}

const api = {
  get: <T>(path: string, signal?: AbortSignal) =>
    request<T>(path, { signal }),

  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", body }),

  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PUT", body }),

  delete: <T>(path: string) =>
    request<T>(path, { method: "DELETE" }),

  upload: <T>(path: string, formData: FormData) =>
    request<T>(path, { method: "POST", body: formData }),
};

export { api, ApiError, getApiBase, getToken, setToken, clearToken, isAuthenticated };
export default api;
